<template>
    <div class="gateway-games-container">
        <div class="gateway-games-content">
            <div class="games-section">
                <h1 class="page-title">{{ title }}</h1>
                <div class="slots-filter-bar">
                    <div class="search-section">
                        <div class="search-input-wrapper">
                            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search games"
                                class="search-input"
                            />
                            <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div v-if="games.length > 0" class="provider-filter-wrap">
                        <label for="slot-provider" class="provider-label">Provider</label>
                        <select
                            id="slot-provider"
                            v-model="selectedProvider"
                            class="provider-select"
                            aria-label="Filter by provider"
                        >
                            <option value="">All Providers</option>
                            <option
                                v-for="p in providerOptions"
                                :key="p"
                                :value="p"
                            >{{ formatProviderOption(p) }}</option>
                        </select>
                    </div>
                </div>

                <div v-if="gameLoading" class="loading-state games-loading">
                    <div class="loading-spinner-simple">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                            </path>
                        </svg>
                        <p class="loading-text-simple">Loading games...</p>
                    </div>
                </div>
                <div v-else-if="error" class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <ButtonGradient @click="loadGames" text="Retry" />
                </div>
                <div v-else-if="filteredGames.length === 0" class="no-games">
                    <p v-if="searchQuery">No games found matching "{{ searchQuery }}"</p>
                    <p v-else-if="selectedProvider">No games for provider "{{ formatProviderOption(selectedProvider) }}"</p>
                    <p v-else>No games available</p>
                </div>
                <div v-else class="games-grid-wrapper">
                    <div class="games-grid">
                        <SlotsGameCard
                            v-for="game in displayedGames"
                            :key="(game.provider_code || '') + '_' + game.code"
                            :game="game"
                            :provider-code="game.provider_code || 'SLOTSGATEWAY'"
                            @launch="launchGame"
                        />
                    </div>
                    <div v-if="hasMoreGames" class="show-more-section">
                        <button type="button" class="show-more-btn" @click="showMoreGames">
                            Show more
                            <span class="show-more-count">(+{{ nextShowMoreCount }} games)</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <transition name="game-modal-fade">
            <div v-if="gameSession" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true" :aria-label="`Playing ${formattedCurrentGame}. Click outside or press Escape to close.`">
                <div class="game-modal" @click.stop role="document">
                    <header class="game-modal-header">
                        <span class="game-modal-title">{{ formattedCurrentGame }}</span>
                        <button type="button" class="game-modal-close" @click.stop="closeGame" aria-label="Close game">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="game-modal-close-text">Close</span>
                        </button>
                    </header>
                    <div class="game-modal-content">
                        <SlotsGameFrame :session="gameSession" />
                    </div>
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ButtonGradient from '@/components/ButtonGradient.vue';
import SlotsGameCard from './SlotsGameCard.vue';
import SlotsGameFrame from './SlotsGameFrame.vue';

export default {
    name: 'GatewayGamesMain',
    components: {
        ButtonGradient,
        SlotsGameCard,
        SlotsGameFrame
    },
    props: {
        title: { type: String, default: 'Games' },
        category: { type: String, required: true } // 'poker' | 'live_casino'
    },
    data() {
        return {
            gameLoading: false,
            searchQuery: '',
            selectedProvider: '',
            gamesDisplayLimit: 21
        };
    },
    computed: {
        ...mapGetters('slots', ['games', 'currentGame', 'gameSession', 'error']),
        /**
         * Safety filter: for Live Casino page, keep only live-casino games even if API/provider
         * returns mixed categories.
         */
        categoryScopedGames() {
            if (String(this.category || '').toLowerCase() !== 'live_casino') {
                return this.games;
            }
            return (this.games || []).filter((game) => this.isLiveCasinoGame(game));
        },
        /** All unique provider names from games (like image: All Providers + 3oaks, 4theplayer, hacksaw, etc.) */
        providerOptions() {
            const values = this.categoryScopedGames
                .map((g) => this.getGameProviderValue(g))
                .filter(Boolean);
            return [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b), 'en', { sensitivity: 'base' }));
        },
        filteredGames() {
            let list = this.categoryScopedGames;
            if (this.selectedProvider) {
                const want = this.selectedProvider.trim().toLowerCase();
                list = list.filter((game) => this.getGameProviderValue(game).toLowerCase() === want);
            }
            if (!this.searchQuery.trim()) return list;
            const query = this.searchQuery.toLowerCase().trim();
            return list.filter(game =>
                (game.name && game.name.toLowerCase().includes(query)) ||
                (game.code && game.code.toLowerCase().includes(query))
            );
        },
        displayedGames() {
            return this.filteredGames.slice(0, this.gamesDisplayLimit);
        },
        hasMoreGames() {
            return this.filteredGames.length > this.gamesDisplayLimit;
        },
        nextShowMoreCount() {
            const remaining = this.filteredGames.length - this.gamesDisplayLimit;
            return Math.min(31, remaining);
        },
        formattedCurrentGame() {
            if (!this.currentGame) return '';
            let name = this.currentGame;
            name = name.replace(/[_-]/g, ' ');
            name = name.replace(/([a-z])([A-Z])/g, '$1 $2');
            name = name.toLowerCase().split(' ').map(word => {
                const uppercaseWords = ['rtp', 'vip', 'ii', 'iii', 'iv', 'vs', 'xl', 'xxi', 'xxl'];
                if (uppercaseWords.includes(word.toLowerCase())) return word.toUpperCase();
                return word.charAt(0).toUpperCase() + word.slice(1);
            }).join(' ');
            return name.replace(/\s+/g, ' ').trim();
        }
    },
    async mounted() {
        await this.loadGames();
        document.addEventListener('keydown', this.handleGameModalKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleGameModalKeydown);
        document.body.classList.remove('slot-game-playing');
    },
    methods: {
        ...mapActions('slots', ['fetchGatewayGamesByCategory', 'resetGameSession', 'handleBalanceUpdate']),
        async loadGames() {
            this.gameLoading = true;
            await this.fetchGatewayGamesByCategory(this.category);
            this.gameLoading = false;
        },
        showMoreGames() {
            this.gamesDisplayLimit += 31;
        },
        closeGame() {
            this.resetGameSession();
        },
        launchGame() {
            const sidebar = document.querySelector('aside#sidebar-left');
            if (sidebar && !sidebar.classList.contains('collapsed') && window.innerWidth > 1024) {
                const toggleButton = sidebar.querySelector('.menu-toggle');
                if (toggleButton) toggleButton.click();
            }
        },
        handleGameModalKeydown(e) {
            if (e.key === 'Escape' && this.gameSession) this.closeGame();
        },
        /** Read provider from game (any common API key) so dropdown gets 3oaks, hacksaw, etc. */
        getGameProviderValue(game) {
            const v = game.provider_code || game.provider_name || game.provider || game.provider_slug ||
                game.category || game.game_provider || game.vendor || game.vendor_name || game.type;
            return (v != null && v !== '') ? String(v).trim() : '';
        },
        /**
         * Detect if a game belongs to live-casino category from common API keys.
         * Excludes obvious non-live categories (slots/poker) when mixed payloads happen.
         */
        isLiveCasinoGame(game) {
            const fields = [
                game?.category,
                game?.game_category,
                game?.category_name,
                game?.type,
                game?.tags,
                game?.provider_code,
                game?.provider_name
            ].filter(v => v != null);

            const blob = fields
                .map((v) => Array.isArray(v) ? v.join(' ') : String(v))
                .join(' ')
                .toLowerCase();

            if (blob.includes('slot') || blob.includes('poker')) return false;
            return blob.includes('live');
        },
        formatProviderOption(value) {
            if (!value) return '';
            return String(value).toLowerCase().replace(/[_\s]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        }
    },
    watch: {
        gameSession(newVal) {
            document.body.classList.toggle('slot-game-playing', !!newVal);
        },
        searchQuery() {
            this.gamesDisplayLimit = 21;
        },
        selectedProvider() {
            this.gamesDisplayLimit = 21;
        }
    }
};
</script>

<style scoped>
.gateway-games-container {
    width: 100%;
    min-height: calc(100vh - 80px);
    padding: var(--spacing-lg);
}

.gateway-games-content {
    max-width: 1400px;
    margin: 0 auto;
}

.page-title {
    font-size: 32px;
    font-weight: 800;
    margin: 0 0 var(--spacing-xl) 0;
    background: var(--accent-main-txt);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 1px;
}

.games-section {
    margin-bottom: var(--spacing-2xl);
    border-radius: 20px;
    padding: 24px;
}

.slots-filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
}

.slots-filter-bar .search-section {
    flex: 1;
    min-width: 200px;
}

.provider-filter-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.provider-filter-wrap .provider-label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
}

.provider-select {
    min-width: 180px;
    padding: 10px 36px 10px 14px;
    font-size: 14px;
    color: #fff;
    background: rgba(22, 26, 34, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-md, 8px);
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.7)' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
}

.provider-select:hover,
.provider-select:focus {
    border-color: rgba(212, 165, 116, 0.5);
    outline: none;
}

.provider-select option {
    background: #1a1e28;
    color: #fff;
}

.search-input-wrapper {
    position: relative;
    max-width: 400px;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7c8a;
    pointer-events: none;
}

.search-input {
    width: 100%;
    height: 48px;
    padding: 0 48px 0 48px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.15);
    outline: none;
    transition: all 0.3s ease;
}

.search-input:focus {
    border-color: var(--color-copper);
    background: rgba(255, 255, 255, 0.08);
}

.search-input::placeholder {
    color: #6b7c8a;
}

.search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;
}

.search-clear:hover {
    background: rgba(255, 255, 255, 0.2);
}

.loading-state, .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    gap: var(--spacing-lg);
}

.loading-spinner-simple {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner-simple svg {
    color: var(--accent-yellow);
}

.loading-text-simple {
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

.error-message {
    color: #ff6b6b;
    font-size: 16px;
    text-align: center;
}

.no-games {
    text-align: center;
    padding: var(--spacing-2xl);
    color: rgba(255, 255, 255, 0.6);
}

.games-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 20px;
    width: 100%;
}

.show-more-section {
    display: flex;
    justify-content: center;
    padding: var(--spacing-md) 0;
}

.show-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: var(--accent-btn-purple, #4a2c7a);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
}

.show-more-btn:hover {
    background: var(--accent-btn-light-purple, #5a3c8a);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.show-more-count { font-size: 13px; opacity: 0.9; }

/* Game modal - reuse slots modal styles */
.game-modal-fade-enter-active,
.game-modal-fade-leave-active { transition: opacity 0.25s ease; }
.game-modal-fade-enter-active .game-modal,
.game-modal-fade-leave-active .game-modal { transition: transform 0.25s ease; }
.game-modal-fade-enter-from,
.game-modal-fade-leave-to { opacity: 0; }
.game-modal-fade-enter-from .game-modal,
.game-modal-fade-leave-to .game-modal { transform: scale(0.97); }

/* Slot game modal: full screen */
.game-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    background: #000;
    border: none;
    border-radius: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10000000 !important;
}

.game-modal-header { display: none !important; }

.game-modal-title {
    font-size: 17px;
    font-weight: 600;
    color: #fff;
}

.game-modal-close {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    cursor: pointer;
}

.game-modal-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: absolute !important;
    top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
    z-index: 1;
}

.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
    pointer-events: auto !important;
}

.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    transform: scale(1.05);
}

@media (max-width: 768px) {
    .games-grid {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
    .page-title { font-size: 24px; }
}
</style>
